/**
 * Teen Patti bot: percentile-based hand strength, pot odds, blind-play tactics and difficulty-tuned bluffing.
 */
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { teenPattiPercentile } from './hands.js';
import { rankOpts, type TeenPattiAction, type TeenPattiLegal, type TeenPattiView } from './engine.js';

function activeOpponents(view: TeenPattiView): number {
  return view.players.filter((p) => p && p.seat !== view.seat && (p.status === 'BLIND' || p.status === 'SEEN')).length;
}

function strength(view: TeenPattiView): number {
  if (view.hand.length !== 3) return 0.5;
  let pct = teenPattiPercentile(view.hand, rankOpts(view.config));
  if (view.config.variant === 'muflis') pct = 1 - pct;
  // Probability of beating every remaining opponent (independence approximation).
  return Math.pow(pct, Math.max(1, activeOpponents(view)));
}

export const teenPattiBot: BotEngine<TeenPattiView, TeenPattiLegal, TeenPattiAction> = {
  decideAction(view, legal, ctx) {
    const me = view.players[view.seat!]!;
    if (legal.respondSideShow) {
      const pct = view.hand.length === 3 ? teenPattiPercentile(view.hand, rankOpts(view.config)) : 0.5;
      return { type: 'sideshowRespond', accept: pct > 0.55 - ctx.profile.aggression * 0.1 };
    }
    if (me.status === 'BLIND') {
      const blindBudget = 1 + Math.round(ctx.profile.aggression * 3);
      const tooExpensive = legal.call !== null && legal.call > me.chips * 0.15;
      if (legal.canSee && (legal.call === null || me.blindsPlayed >= blindBudget || tooExpensive || chance(ctx, 0.25))) {
        return { type: 'see' };
      }
      if (legal.call !== null) {
        if (legal.raise !== null && chance(ctx, ctx.profile.aggression * 0.25)) return { type: 'bet', raise: true };
        return { type: 'bet', raise: false };
      }
      if (legal.show !== null) return { type: 'show' };
      return { type: 'pack' };
    }
    if (this.chooseFold!(view, legal, ctx)) return { type: 'pack' };
    return this.chooseBet!(view, legal, ctx);
  },

  chooseFold(view, legal, ctx) {
    const s = strength(view);
    const cost = legal.call ?? legal.show ?? Infinity;
    if (cost === Infinity) return true;
    const potOdds = cost / (view.pot + cost);
    const bluffing = chance(ctx, ctx.profile.bluff);
    if (bluffing) return false;
    if (chance(ctx, ctx.profile.mistakeRate)) return chance(ctx, 0.5);
    return s < potOdds * (1.2 - ctx.profile.aggression * 0.4);
  },

  chooseBet(view, legal, ctx) {
    const s = strength(view);
    const heads = activeOpponents(view) === 1;
    if (heads && legal.show !== null) {
      // Show with good hands, or when continuing is getting expensive relative to the stack.
      const me = view.players[view.seat!]!;
      if (s > 0.62 || legal.call === null || legal.call > me.chips * 0.3) return { type: 'show' };
    }
    if (legal.sideShow !== null && s > 0.25 && s < 0.55 && chance(ctx, 0.5)) return { type: 'sideshow' };
    if (legal.raise !== null && (s > 0.78 - ctx.profile.aggression * 0.15 || chance(ctx, ctx.profile.bluff * 0.5))) {
      return { type: 'bet', raise: true };
    }
    if (legal.call !== null) return { type: 'bet', raise: false };
    if (legal.show !== null) return { type: 'show' };
    return { type: 'pack' };
  },

  evaluateState: (view) => strength(view),

  calculateRisk(view, legal) {
    const me = view.players[view.seat!];
    const cost = legal.raise ?? legal.call ?? 0;
    return me && me.chips > 0 ? Math.min(1, cost / me.chips) : 1;
  },

  handleTimeout(_view, legal) {
    if (legal.respondSideShow) return { type: 'sideshowRespond', accept: false };
    return { type: 'pack' };
  },
};

export type { BotContext };
